/**
 * Deep clone that also strips Vue reactive proxies (toRaw) in one pass.
 *
 * Contract: intended for plain JSON-ish data (objects, arrays, primitives).
 * `Date`, `Map`, and `Set` are cloned with their contents; other class
 * instances (e.g. Fabric objects) are NOT supported and will be cloned as
 * plain objects of their enumerable own keys. Circular references are
 * preserved instead of overflowing the stack.
 */

import { toRaw } from 'vue';

export function deepCloneRaw<T>(obj: T): T {
  return cloneInner(obj, new WeakMap());
}

function cloneInner<T>(obj: T, seen: WeakMap<object, unknown>): T {
  const raw = toRaw(obj);
  if (raw === null || typeof raw !== 'object') return raw;

  const existing = seen.get(raw as object);
  if (existing !== undefined) return existing as T;

  if (raw instanceof Date) {
    const copy = new Date(raw);
    seen.set(raw, copy);
    return copy as T;
  }

  if (raw instanceof Map) {
    const copy = new Map();
    seen.set(raw, copy);
    for (const [k, v] of raw) {
      copy.set(cloneInner(k, seen), cloneInner(v, seen));
    }
    return copy as T;
  }

  if (raw instanceof Set) {
    const copy = new Set();
    seen.set(raw, copy);
    for (const v of raw) {
      copy.add(cloneInner(v, seen));
    }
    return copy as T;
  }

  if (Array.isArray(raw)) {
    const copy: unknown[] = [];
    seen.set(raw, copy);
    for (const item of raw) {
      copy.push(cloneInner(item, seen));
    }
    return copy as T;
  }

  const result: Record<string, unknown> = {};
  seen.set(raw, result);
  for (const key of Object.keys(raw)) {
    result[key] = cloneInner((raw as Record<string, unknown>)[key], seen);
  }
  return result as T;
}
